import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { api } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ArrowLeft, Target, Clock, CheckCircle2, Loader2, Play, FileText, ChevronRight } from 'lucide-react';

const WeightedAssessmentsHubPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [past, setPast] = useState([]);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const isP6 = user?.level === 'P6';

  useEffect(() => {
    (async () => {
      try {
        const data = await api.waMine();
        setPast(data.weighted_assessments || []);
      } catch (e) {
        // silent
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleStart = async () => {
    if (!isP6) {
      toast.error('Weighted Assessments are P6-only for now. Please switch level in your profile.');
      return;
    }
    setStarting(true);
    const t = toast.loading('Generating your Weighted Assessment paper… this takes 30-60 seconds.');
    try {
      const wa = await api.waStart();
      toast.success('Paper ready! Timer starting…', { id: t });
      navigate(`/weighted-assessments/${wa.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to start WA', { id: t });
    } finally {
      setStarting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-5xl mx-auto px-4 md:px-8 py-8">
        <button
          onClick={() => navigate('/dashboard')}
          className="flex items-center gap-1.5 text-slate-600 hover:text-emerald-700 font-semibold text-sm mb-6"
          data-testid="wa-back"
        >
          <ArrowLeft size={16} /> Back to dashboard
        </button>

        <div className="mb-8">
          <div className="inline-flex items-center gap-1.5 bg-rose-100 text-rose-800 text-xs font-black uppercase tracking-wider px-3 py-1.5 rounded-full mb-3">
            <Target size={12} /> Exam simulation · P6
          </div>
          <h1 className="text-3xl md:text-4xl font-heading font-black text-slate-900 mb-2">Weighted Assessment</h1>
          <p className="text-slate-600 max-w-2xl">
            A full timed P6 English Paper 2 simulation. 90 marks across 6 topics in two sections, 1 hour 50 minutes.
            Auto-graded (with AI marking for open-ended answers) and detailed per-topic feedback at the end.
          </p>
        </div>

        <Card className="p-6 md:p-8 rounded-3xl border-2 border-emerald-200 mb-10 bg-gradient-to-br from-emerald-50 to-white">
          <div className="grid md:grid-cols-2 gap-6 mb-6">
            <div>
              <div className="text-xs font-black uppercase tracking-wide text-emerald-700 mb-2">Section A · 25 marks</div>
              <ul className="text-sm text-slate-700 space-y-1">
                <li>· Vocabulary MCQ — 10 marks</li>
                <li>· Vocabulary Cloze — 10 marks</li>
                <li>· Visual Text — 5 marks</li>
              </ul>
            </div>
            <div>
              <div className="text-xs font-black uppercase tracking-wide text-emerald-700 mb-2">Section B · 65 marks</div>
              <ul className="text-sm text-slate-700 space-y-1">
                <li>· Comprehension Cloze — 30 marks</li>
                <li>· Editing — 12 marks</li>
                <li>· Comprehension Open — 23 marks</li>
              </ul>
            </div>
          </div>

          <div className="flex items-center gap-3 flex-wrap mb-4 text-xs text-slate-600">
            <span className="inline-flex items-center gap-1"><Clock size={12}/> 1 hour 50 minutes</span>
            <span>·</span>
            <span className="inline-flex items-center gap-1"><FileText size={12}/> 58 items · 90 marks</span>
            <span>·</span>
            <span className="inline-flex items-center gap-1"><CheckCircle2 size={12}/> Auto-graded</span>
          </div>

          <Button
            onClick={handleStart}
            disabled={starting || !isP6}
            className="bg-emerald-600 hover:bg-emerald-700 text-white font-black rounded-2xl px-6 py-6 text-base w-full md:w-auto"
            data-testid="wa-start-btn"
          >
            {starting ? (
              <><Loader2 className="animate-spin mr-2" size={18}/> Preparing paper…</>
            ) : (
              <><Play className="mr-2" size={18}/> Start Weighted Assessment</>
            )}
          </Button>
          {!isP6 && (
            <p className="text-xs text-rose-600 mt-3">
              Weighted Assessments are P6-only for now. Change your profile level to P6 to attempt one.
            </p>
          )}
        </Card>

        <h2 className="text-xl font-heading font-bold text-slate-800 mb-3">My past assessments</h2>
        {loading ? (
          <div className="text-slate-500 text-sm">Loading…</div>
        ) : past.length === 0 ? (
          <Card className="p-8 rounded-3xl border-2 border-dashed border-slate-300 text-center">
            <Target className="mx-auto mb-3 text-slate-400" size={32} />
            <p className="text-slate-600 text-sm">No past assessments yet. Start your first one above.</p>
          </Card>
        ) : (
          <div className="grid gap-3">
            {past.map((w) => (
              <motion.button
                key={w.id}
                whileHover={{ x: 2 }}
                onClick={() => navigate(`/weighted-assessments/${w.id}`)}
                className="text-left bg-white border-2 border-slate-200 hover:border-emerald-300 rounded-2xl px-4 py-3 flex items-center justify-between transition-colors"
                data-testid={`wa-past-${w.id}`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                    w.status === 'submitted' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
                  }`}>
                    {w.status === 'submitted' ? <CheckCircle2 size={18}/> : <Clock size={18}/>}
                  </div>
                  <div>
                    <div className="font-bold text-slate-800 text-sm">
                      P6 Weighted Assessment · {w.status === 'submitted' ? 'Completed' : 'In progress'}
                    </div>
                    <div className="text-xs text-slate-500">
                      {new Date(w.started_at).toLocaleString('en-SG', { dateStyle: 'medium', timeStyle: 'short' })}
                      {w.result && ` · Score: ${w.result.total_scored}/${w.result.total_marks} (${w.result.percentage}%)`}
                    </div>
                  </div>
                </div>
                <ChevronRight size={18} className="text-slate-400" />
              </motion.button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default WeightedAssessmentsHubPage;
